<template>
  <!-- Top Bar -->
  <div class="flex relative h-16 mx-4 sm:mx-12 pt-6">
    <button
      @click="() => emits('mobile-open')"
      type="button"
      class="px-4 text-gray-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:hidden"
    >
      <span class="sr-only">Open sidebar</span>
      <!-- Heroicon name: outline/menu-alt-2 -->
      <svg
        class="h-6 w-6"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 6h16M4 12h16M4 18h7"
        />
      </svg>
    </button>

    <div class="flex gap-x-3 items-center flex-1 relative">
      <!-- Start Search -->
      <div class="relative flex flex-1 text-gray-700 items-center z-30">
        <label
          for="desktop-search"
          class="sr-only"
        >
          Search
        </label>
        <input
          id="search"
          ref="searchBox"
          type="search"
          placeholder="Search"
          v-model="searchQuery"
          autocomplete="off"
          class="block w-full max-w-xl border-[1.5px] border-gray-400 rounded-lg pl-14 text-gray-700 sm:text-lg font-normal focus:ring-0 py-2 placeholder-gray-700"
          @keydown.up.prevent="highlightPrevious"
          @keydown.down.prevent="highlightNext"
          @keydown.enter="gotoLink"
        />
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center">
          <div class="border-r border-gray-400 h-5 px-3">
            <svg
              class="h-5 w-5 relative left-0.5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Search results -->
      <div
        v-if="searchQuery"
        class="z-20 right-0 left-0 overflow-y-auto overflow-x-hidden max-w-xl shadow-lg bg-gray-100 rounded-b-lg border border-gray-400"
        :class="{
          'absolute top-8 pt-3 search-max-h': !screenIsSm,
          'fixed top-20 mobile-search-max-h': screenIsSm
        }"
      >
        <div class="flex-1 relative ml-0 md-ml-5">
          <ul
            ref="results"
            class=""
          >
            <li
              v-if="searching"
              class="p-3 font-medium border-b border-gray-400"
            >
              Searching...
            </li>
            <li
              v-if="!searching"
              class="p-3 font-medium border-b border-gray-400"
            >
              Showing {{ searchResults.length }} results
            </li>
            <li
              v-if="!searching"
              v-for="(item, index) in searchResults"
              :key="`${item.type}-${item.id}`"
              :class="{ 'bg-gray-400': index === highlightedIndex }"
              @keyup.enter="gotoLink"
              class="hover:bg-gray-300 p-3 border-b border-gray-400 flex justify-between items-center cursor-pointer"
              tabindex="0"
            >
              <div class="leading-tight">
                <h5
                  class="font-semibold text-base text-blue-600 cursor-pointer"
                  @click="selectSearchItem(item)"
                >
                  <span v-if="item.type === 'product'">Product #</span>
                  <span v-else-if="item.type === 'package'">Package #</span>
                  <span v-else-if="item.type === 'customer'">Customer #</span>
                  <span v-else-if="item.type === 'order'">Order #</span>
                  <span v-else>Item #</span>
                  <span>{{ item.id }}: {{ item.name }}</span>
                </h5>
                <small
                  class="font-semibold text-sm text-gray-600 cursor-pointer"
                  @click="selectSearchItem(item)"
                >
                  <span v-if="item.type === 'product'">SKU: </span>
                  <span v-else-if="item.type === 'package'">Sub-Lot: </span>
                  <span v-else-if="item.type === 'customer'">Email: </span>
                  <span v-else-if="item.type === 'order'">Ref #: </span>
                  <span v-else>Ref: </span>
                  {{ item.ref }}
                </small>
              </div>
              <div v-if="item.type === 'customer' && !item.has_active_visit">
                <a
                  @click.prevent="checkInCustomer(item)"
                  class="border border-gray-600 text-blue-600 px-4 py-1 bg-gray-100 font-semibold rounded-full hover:bg-blue-600 hover:text-gray-100 mr-2"
                  href="#"
                >
                  Check-In
                </a>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <!-- End search results -->

      <!-- End Search -->

      <div class="flex items-center gap-x-3">
        <HelpButton />

        <!-- User avatar dropdown -->
        <Menu
          as="div"
          class="relative inline-block text-left"
        >
          <div>
            <MenuButton
              class="btn h-[42px] p-3 text-blue-600 font-semibold drop-shadow-sm border-gray-400 focus:border-blue-600 hover:border-blue-600 hover:bg-blue-600 hover:text-gray-100"
            >
              {{ userInitials || 'You' }}
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 z-20 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 focus:outline-none divide-y divide-gray-400"
            >
              <div class="px-4 py-3 text-right">
                <p class="text-sm">Signed in as</p>
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ email || 'You' }}
                </p>
              </div>
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <Link
                    :href="route('user.edit')"
                    class="font-base block px-4 py-2 text-sm text-gray-700 text-right hover:text-blue-600"
                  >
                    My Profile
                  </Link>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <Link
                    :href="route('support')"
                    class="font-base block px-4 py-2 text-sm text-gray-700 text-right hover:text-blue-600"
                  >
                    Support
                  </Link>
                </MenuItem>
              </div>
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    :href="route('logout')"
                    class="w-full text-right font-base block px-4 py-2 text-sm text-gray-700 hover:text-blue-600"
                  >
                    Sign Out
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
      <!-- End user avatar dropdown -->
    </div>
  </div>
  <!-- End Top Bar -->
</template>

<style scoped>
.search-max-h {
  max-height: 80vh;
}
.mobile-search-max-h {
  max-height: calc(100vh - theme('spacing.20'));
}
</style>

<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import HelpButton from '@/Components/HelpButton.vue'
import { screenIsSm } from '@/Composables/breakpoints'
const emits = defineEmits(['mobile-open'])
</script>

<script>
// @Note: This is ported more or less as-is from the old layout.
import { Link, useForm } from '@inertiajs/vue3'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import hasAuth from '@/Mixins/hasAuth'
import search from '../../json/product.json'

export default {
  mixins: [hasAuth],
  components: {
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems
  },
  data() {
    return {
      // Global search
      searchQuery: null,
      searching: false,
      searchResults: [],
      highlightedIndex: 0
    }
  },

  mounted() {
    document.addEventListener('keyup', this.keyupHandler)
    document.addEventListener('keydown', this.keyupHandler)
  },

  watch: {
    searchQuery: function (newVal, oldVal) {
      if (!newVal) {
        this.clearSearch()
      } else {
        this.search()
      }
    }
  },

  methods: {
    search: _.debounce(
      function () {
        this.searching = true

        axios.post(this.route('search'), { query: this.searchQuery }).then((response) => {
          this.searchResults = response.data.data || []
          this.searching = false
        })
      },
      500,
      {
        leading: false,
        trailing: true
      }
    ),

    clearSearch() {
      this.searchQuery = null
      this.searchResults = []
      this.searching = false
      this.highlightedIndex = 0
    },

    selectSearchItem(item) {
      this.clearSearch()

      if (item.url) {
        window.location.href = item.url
      }
    },
    gotoLink() {
      if (this.searchResults[this.highlightedIndex]) {
        window.location = this.searchResults[this.highlightedIndex].url
      }
    },
    highlightPrevious() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex = this.highlightedIndex - 1
        this.scrollIntoView()
      }
    },
    highlightNext() {
      if (this.highlightedIndex < this.searchResults.length - 1) {
        this.highlightedIndex = this.highlightedIndex + 1
        this.scrollIntoView()
      }
    },
    scrollIntoView() {
      this.$refs.results.children[this.highlightedIndex].scrollIntoView({ block: 'nearest' })
    },
    focusSearch() {
      this.$refs.searchBox.focus()
    },
    keyupHandler(event) {
      // console.log(event);
      if (event.ctrlKey && event.code === 'KeyK') {
        this.focusSearch()
      }
    }
  }
}
</script>
